// Import form page — 3-step wizard: supplier → items → preview+commit.
// Route: /inventory/imports/new  and  /inventory/imports/:id
import { useState, useEffect, useRef, Fragment } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/ui/money-input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { useScannerAutoSubmit } from "./use-scanner-auto-submit";
import { ImportFormStepOne, type Step1Data } from "./import-form-step-one";
import { ImportFormStepTwo, type ImportStepTwoHandle, EditableCostCell } from "./import-form-step-two";
import { SerialHistoryModal } from "./serial-history-modal";
import { ImportPaymentsSection } from "./import-payments-section";

interface StockImport {
  id: string;
  code: string;
  status: string;
  supplierId?: string | null;
  supplierName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  totalCost: string;
  note: string;
  debtBooked: boolean;
  createdAt: string;
  committedAt: string | null;
}

interface ImportDetail {
  import: StockImport;
  items: Array<{
    id: string;
    productName: string | null;
    variantSku: string | null;
    quantity: number;
    unitCost: string;
    discount: string;
    note: string;
    serials: string[];
  }>;
}

function fmtCost(cost: string) {
  const n = parseFloat(cost);
  if (isNaN(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n);
}

const STEPS = ["Thông tin", "Thêm hàng", "Xác nhận"];

// Gắn IMEI bổ sung cho dòng thiếu serial của phiếu ĐÃ xác nhận — lô "hàng đếm
// đủ nhưng file hãng thiếu/sai IMEI": chít số thật trên vỏ hộp khi bán tới.
function AttachSerialArea({
  importId,
  itemId,
  missing,
}: {
  importId: string;
  itemId: string;
  missing: number;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  const attach = useMutation({
    mutationFn: (serial: string) =>
      apiClient.post(`/v1/imports/${importId}/items/${itemId}/serials`, { serials: [serial] }),
    onSuccess: () => {
      setValue("");
      setError("");
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
    },
    onError: (e) => setError(translateApiError(e)),
  });

  function submit() {
    const s = value.trim();
    if (!s || attach.isPending) return;
    attach.mutate(s);
  }

  // Máy quét không gửi Enter → tự gắn khi vừa tít xong.
  useScannerAutoSubmit(value, submit, { enabled: !attach.isPending });

  return (
    <div className="space-y-1 py-1">
      <p className="text-xs text-amber-600 dark:text-amber-400">
        Dòng này còn thiếu {missing} IMEI — chít số thật trên vỏ hộp để gắn bổ sung:
      </p>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Quét IMEI (tự gắn) hoặc gõ rồi Enter"
          className="h-8 max-w-xs font-mono text-xs"
          autoComplete="off"
        />
        <Button size="sm" variant="outline" disabled={!value.trim() || attach.isPending} onClick={submit}>
          {attach.isPending ? "Đang gắn…" : "Gắn"}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function ImportFormPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState(params.id ? 1 : 0);
  const [importId, setImportId] = useState<string | null>(params.id ?? null);
  const [step1, setStep1] = useState<Step1Data>({ supplierId: "", warehouseId: "", note: "" });
  // True when the add-item form still holds a typed/scanned line not yet added.
  const [pendingItem, setPendingItem] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const stepTwoRef = useRef<ImportStepTwoHandle>(null);
  // Xem IMEI ở màn xem lại phiếu: dòng đang mở + IMEI đang tra lịch sử.
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [serialView, setSerialView] = useState<string | null>(null);

  // Auto-add the line still sitting in the form, then go to preview — staff never
  // have to remember "Thêm vào phiếu" (a common way to drop an item, e.g. cục nóng).
  async function goPreview() {
    setFlushing(true);
    try {
      await stepTwoRef.current?.flushPending();
    } catch {
      // Add failed — the form surfaces the error; stay on this step.
      setFlushing(false);
      return;
    }
    setFlushing(false);
    setStep(2);
  }

  // Load existing import detail when on step 2/3
  const { data: detail } = useQuery({
    queryKey: ["import-detail", importId],
    queryFn: () =>
      apiClient
        .get<{ data: ImportDetail }>(`/v1/imports/${importId}`)
        .then((r) => r.data.data),
    enabled: !!importId,
  });

  const si = detail?.import;
  const items = detail?.items ?? [];
  const isDraft = !si || si.status === "draft";

  // A committed/cancelled import is read-only — jump straight to the review view
  // instead of getting stuck on the (draft-only) "add items" step.
  useEffect(() => {
    if (si && si.status !== "draft") setStep(2);
  }, [si?.id, si?.status]);

  // Step 0 → create draft
  const [createError, setCreateError] = useState("");
  const createMutation = useMutation({
    mutationFn: (body: object) =>
      apiClient.post<{ data: StockImport }>("/v1/imports", body).then((r) => r.data.data),
    onSuccess: (created) => {
      setCreateError("");
      setImportId(created.id);
      qc.invalidateQueries({ queryKey: ["imports"] });
      setStep(1);
      void navigate({ to: "/inventory/imports/$id", params: { id: created.id } });
    },
    onError: (e) => setCreateError(translateApiError(e)),
  });

  // Step 2 → commit
  // Sua gia mot dong — cho ca phieu DA NHAP (ke toan dien gia sau khi
  // "nhap tam gia 0"). BE tu tinh lai tong phieu.
  // Gửi đúng ô vừa sửa ({unitCost} hoặc {discount}) — BE giữ nguyên trường vắng mặt.
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: object }) =>
      apiClient.patch(`/v1/imports/${importId}/items/${itemId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
      qc.invalidateQueries({ queryKey: ["imports"] });
    },
  });

  // "Trả tiền NCC ngay" khi xác nhận phiếu — kế toán khỏi phải mở lại phiếu
  // sau commit để ghi chi. Commit xong mới gọi API payments (API yêu cầu
  // phiếu committed); nếu ghi chi lỗi thì phiếu VẪN đã nhập, báo rõ để ghi
  // lại ở khối Thanh toán NCC bên dưới.
  const user = useAuthStore((s) => s.user);
  const canPayNow =
    user?.role === "admin" || user?.role === "manager" || user?.role === "sales";
  // Viewer (chị Thủy) chỉ XEM phiếu: không ô sửa giá/CK, không nút xác nhận.
  const canEditImport = user?.role !== "viewer";
  const [payNowAmount, setPayNowAmount] = useState("");
  const [payNowMethod, setPayNowMethod] = useState<"cash" | "bank_manual">("cash");
  const [payNowError, setPayNowError] = useState("");

  const payNowMutation = useMutation({
    mutationFn: (body: { amount: number; method: string; note: string }) =>
      apiClient.post(`/v1/imports/${importId}/payments`, body),
    onSuccess: () => {
      setPayNowAmount("");
      setPayNowError("");
      qc.invalidateQueries({ queryKey: ["import-payments", importId] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
    },
    onError: (e) =>
      setPayNowError(
        `Phiếu ĐÃ nhập thành công nhưng ghi chi trả NCC chưa được: ${translateApiError(e)}. Ghi lại ở khối "Thanh toán NCC" bên dưới.`,
      ),
  });

  const commitMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/v1/imports/${importId}/commit`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["imports"] });
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
      qc.invalidateQueries({ queryKey: ["stock"] });
      const amt = parseFloat(payNowAmount);
      if (amt > 0) {
        payNowMutation.mutate({
          amount: amt,
          method: payNowMethod,
          note: "Trả NCC khi nhập hàng",
        });
      }
    },
  });

  function handleCreateDraft() {
    createMutation.mutate({
      supplierId: step1.supplierId || null,
      warehouseId: step1.warehouseId,
      note: step1.note,
    });
  }

  // Thành tiền dòng = SL × đơn giá − chiết khấu (CK là tiền cho cả dòng).
  const totalPreview = items.reduce((acc, item) => {
    return (
      acc +
      (parseFloat(item.unitCost) || 0) * item.quantity -
      (parseFloat(item.discount ?? "0") || 0)
    );
  }, 0);

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-semibold">
        {importId ? `Phiếu nhập ${si?.code ?? "…"}` : "Tạo phiếu nhập"}
      </h1>

      {/* Step indicator */}
      <div className="flex gap-2">
        {STEPS.map((label, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 text-sm ${i === step ? "font-semibold text-primary" : "text-muted-foreground"}`}
          >
            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs
              ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-green-500 text-white" : "bg-muted"}`}>
              {i < step ? "✓" : i + 1}
            </span>
            {label}
            {i < STEPS.length - 1 && <span className="text-muted-foreground">›</span>}
          </div>
        ))}
      </div>

      {/* Step 0: Supplier + note */}
      {step === 0 && (
        <div className="space-y-4">
          <ImportFormStepOne data={step1} onChange={setStep1} />
          {createError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{createError}</p>
          )}
          {!step1.warehouseId && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              Chưa chọn <b>kho nhập</b> — phải biết hàng về kho nào mới tạo được phiếu.
            </p>
          )}
          <Button
            onClick={handleCreateDraft}
            disabled={createMutation.isPending || !step1.warehouseId}
          >
            {createMutation.isPending ? "Đang tạo…" : "Tiếp theo →"}
          </Button>
        </div>
      )}

      {/* Step 1: Add items */}
      {step === 1 && importId && isDraft && (
        <div className="space-y-4">
          <ImportFormStepTwo importId={importId} ref={stepTwoRef} onPendingChange={setPendingItem} />
          {pendingItem && (
            <p className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
              ℹ️ Đang có 1 mặt hàng nhập dở. Bấm <b>"Thêm mặt hàng này & nhập tiếp"</b> để nhập thêm sản phẩm khác, hoặc bấm <b>"Xem trước"</b> — hệ thống sẽ <b>tự thêm</b> mặt hàng này vào phiếu.
            </p>
          )}
          {items.length === 0 && !pendingItem && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              Phiếu chưa có mặt hàng nào — quét IMEI, gõ tên sản phẩm hoặc upload file Excel để thêm.
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(0)}>← Quay lại</Button>
            <Button onClick={goPreview} disabled={(items.length === 0 && !pendingItem) || flushing}>
              {flushing ? "Đang thêm…" : "Xem trước →"}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Preview + commit */}
      {step === 2 && si && (
        <div className="space-y-4">
          <div className="rounded-md border p-4 space-y-2 bg-muted/20">
            <p className="text-sm"><span className="text-muted-foreground">Mã phiếu:</span> {si.code}</p>
            <p className="text-sm"><span className="text-muted-foreground">Kho nhập:</span> {si.warehouseName ?? "—"}</p>
            <p className="text-sm"><span className="text-muted-foreground">Nhà cung cấp:</span> {si.supplierName ?? "—"}</p>
            <p className="text-sm"><span className="text-muted-foreground">Số mặt hàng:</span> {items.length}</p>
            <p className="text-sm font-semibold">
              Tổng tiền: {fmtCost(String(totalPreview))}
            </p>
          </div>

          {/* Item list — lets the user review exactly what this phiếu contains. */}
          {items.length > 0 && (
            <Table>
              <THead>
                <Tr>
                  <Th>SKU</Th>
                  <Th>Sản phẩm</Th>
                  <Th className="text-right">SL</Th>
                  <Th className="text-right">Đơn giá</Th>
                  <Th className="text-right">Chiết khấu</Th>
                  <Th className="text-right">Thành tiền</Th>
                  <Th>Ghi chú</Th>
                  <Th className="text-right">IMEI/Serial</Th>
                </Tr>
              </THead>
              <TBody>
                {items.map((item) => {
                  const serialCount = item.serials?.length ?? 0;
                  const open = expandedItem === item.id;
                  return (
                    <Fragment key={item.id}>
                      <Tr>
                        <Td className="font-mono text-xs">{item.variantSku ?? "—"}</Td>
                        <Td>{item.productName ?? "—"}</Td>
                        <Td className="text-right">{item.quantity}</Td>
                        <Td className="text-right">
                      {/* Phieu da nhap van sua duoc gia (nhap tam gia 0 -> dien sau) */}
                      {si.status !== "cancelled" && canEditImport ? (
                        <EditableCostCell
                          initial={item.unitCost}
                          onSave={(raw) => {
                            const v = raw.trim();
                            if (v !== "" && parseFloat(v) !== parseFloat(item.unitCost)) {
                              updateItemMutation.mutate({ itemId: item.id, body: { unitCost: v } });
                            }
                          }}
                        />
                      ) : (
                        fmtCost(item.unitCost)
                      )}
                    </Td>
                        <Td className="text-right">
                          {si.status !== "cancelled" && canEditImport ? (
                            <EditableCostCell
                              initial={item.discount ?? "0"}
                              onSave={(raw) => {
                                const v = raw.trim();
                                if (v !== "" && parseFloat(v) !== parseFloat(item.discount ?? "0")) {
                                  updateItemMutation.mutate({ itemId: item.id, body: { discount: v } });
                                }
                              }}
                            />
                          ) : (
                            fmtCost(item.discount ?? "0")
                          )}
                        </Td>
                        <Td className="text-right">
                          {fmtCost(
                            String(
                              (parseFloat(item.unitCost) || 0) * item.quantity -
                                (parseFloat(item.discount ?? "0") || 0),
                            ),
                          )}
                        </Td>
                        <Td className="max-w-[140px] break-words text-xs">{item.note || "—"}</Td>
                        <Td className="text-right">
                          {serialCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => setExpandedItem(open ? null : item.id)}
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              <ChevronRight size={13} className={`transition-transform ${open ? "rotate-90" : ""}`} />
                              {serialCount} IMEI
                            </button>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </Td>
                      </Tr>
                      {si.status === "committed" && canEditImport &&
                        serialCount < item.quantity && (
                          <Tr>
                            <Td colSpan={8} className="bg-amber-50/60 dark:bg-amber-950/20">
                              <AttachSerialArea
                                importId={si.id}
                                itemId={item.id}
                                missing={item.quantity - serialCount}
                              />
                            </Td>
                          </Tr>
                        )}
                      {open && serialCount > 0 && (
                        <Tr>
                          <Td colSpan={8} className="bg-muted/20">
                            <div className="flex flex-wrap gap-1.5 py-1">
                              {item.serials.map((sn) => (
                                <button
                                  key={sn}
                                  type="button"
                                  onClick={() => setSerialView(sn)}
                                  className="rounded border bg-background px-2 py-0.5 font-mono text-xs text-primary hover:underline"
                                >
                                  {sn}
                                </button>
                              ))}
                            </div>
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  );
                })}
              </TBody>
            </Table>
          )}

          {/* Thanh toán NCC — chỉ phiếu đã xác nhận và có NCC (không NCC thì không biết trả ai) */}
          {si.status === "committed" && si.supplierName && (
            <ImportPaymentsSection
              importId={si.id}
              totalCost={si.totalCost}
              supplierName={si.supplierName}
              debtBooked={si.debtBooked}
            />
          )}

          {si.status === "committed" ? (
            <p className="text-green-700 font-medium">Phiếu đã được nhập thành công.</p>
          ) : !canEditImport ? (
            <p className="text-muted-foreground">Phiếu nháp — tài khoản của bạn chỉ xem.</p>
          ) : (
            <>
              {canPayNow && si.supplierName && (
                <div className="space-y-2 rounded-md border p-3 bg-muted/20">
                  <p className="text-sm font-medium">
                    Trả tiền NCC ngay khi nhập (tùy chọn) — {si.supplierName}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <MoneyInput
                      value={payNowAmount}
                      onChange={setPayNowAmount}
                      placeholder="Số tiền trả NCC"
                      className="w-40"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPayNowAmount(String(Math.round(totalPreview)))}
                    >
                      Trả đủ ({fmtCost(String(totalPreview))})
                    </Button>
                    <select
                      value={payNowMethod}
                      onChange={(e) => setPayNowMethod(e.target.value as "cash" | "bank_manual")}
                      className="h-9 rounded-md border bg-background px-2 text-sm"
                    >
                      <option value="cash">Tiền mặt</option>
                      <option value="bank_manual">Chuyển khoản</option>
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Để trống = chưa trả (toàn bộ ghi công nợ NCC). Số tiền này sẽ được ghi
                    sổ chi ngay sau khi xác nhận phiếu.
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>← Sửa</Button>
                <Button
                  onClick={() => commitMutation.mutate()}
                  disabled={commitMutation.isPending || items.length === 0}
                >
                  {commitMutation.isPending ? "Đang xử lý…" : "Xác nhận nhập hàng"}
                </Button>
              </div>
            </>
          )}

          {commitMutation.isSuccess && (
            <p className="text-green-700">Nhập kho thành công! Tồn kho đã được cập nhật.</p>
          )}
          {payNowMutation.isSuccess && (
            <p className="text-green-700">Đã ghi chi trả NCC.</p>
          )}
          {payNowError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{payNowError}</p>
          )}
          {commitMutation.isError && (
            <p className="text-destructive">Có lỗi xảy ra. Vui lòng thử lại.</p>
          )}
        </div>
      )}

      {serialView && (
        <SerialHistoryModal serialNo={serialView} onClose={() => setSerialView(null)} />
      )}
    </div>
  );
}
